import { ChangeDetectionStrategy, Component, ElementRef, Input, OnDestroy, OnInit, Renderer2, effect, inject } from '@angular/core';
import { Catalog } from '@df/catalog/catalog.interfaces';
import { app } from '@df/core/app';
import { Meta } from '@df/core/meta/meta';
import { CustomerModel } from '@df/session/api/customer.model';
import { BgColorOptions, ColorOptions } from '@df/ui/atomic/atomic-options';
import { AfterContentInitDirectiveService } from '@df/ui/common/after-content-init.directive';
import { PriceService } from '@df/ui/price/price.service';
import { ToggleService } from '@df/ui/toggle/toggle.service';
import { UiComponent } from '@df/ui/ui.component';
import { ScrollEvent, ScrollService } from '@df/util/service/scroll.service';
import { ICMS_CLIENT_CONFIG, NICmsClientConfig } from '@icms/client/config/icms-client.config';
import { ICmsComponent, ICmsContent, NICmsContentComponent, NICmsContentField } from '@icms/core/content';
import { ICmsPage } from '@icms/core/resource/page/icms-page';
import { BehaviorSubject } from 'rxjs';
import { NCmsMinibasket } from '../../basket/minibasket/minibasket.component';
import { BranchService } from '../../branch/branch.service';
import { NCmsBranchCollection } from '../../branch/collection/branch-collection.component';
import { NPageStoreFinderPageType } from '../../catalog/page/store-finder/page-store-finder.component';
import { DeliveryPostcodeService } from '../../common/delivery/delivery-postcode.service';
import { NCmsDelivery } from '../../common/delivery/modal/delivery-modal.component';
import { iconsOptions } from '../../styles/cms.config';
import { NPageOffCanvas } from '../off-canvas/page-off-canvas.component';
import { PageOffCanvasSearchFormComponent } from '../off-canvas/search-form/page-off-canvas-search-form.component';
export const PAGE_HEADER_CONTENT_ELEMENT = 'page-header-content';

export namespace NPageHeader {
    export const BLOCK_ID = 'page-header';

    export interface IBlockData extends Catalog.IBlockData<IBlockDataContent> {}

    export interface IBlockDataContent {
        /**
         * Logo image
         */
        logo: string;
        /**
         * Shop submenu icon
         */
        shopSubmenuIcon?: string;
        /**
         * Shop submenu
         */
        shopSubmenu: IShopSubmenuItem[];
        /**
         * Meganav menu
         */
        meganav: IMeganavItem[];

        /**
         * If promo bar is enabled
         */
        promobannerEnabled: boolean;
        /**
         * Background color
         */
        promobannerBackround: string;
        /**
         * Text color
         */
        promobannerColor: string;
        /**
         * Blocks structure of advanced promo banner
         */
        promobannerBlocks: IPromobarContentBlock[];
        hideTab?: boolean;
        firstActivated?: boolean;
        firstTabImage?: string;
        firstTabLink?: string;
        secondTabImage?: string;
        secondTabLink?: string;
        thirdTabImage?: string;
        thirdTabLink?: string;
        topLinks?: IMenuFields[];
    }

    /**
     * Shop submenu entry
     */
    export interface IShopSubmenuItem extends IMenuFields {
        title: string;
        link?: string;
        submenuLinkCopy?: string;
        submenuLink?: string;
        submenus?: IShopSubmenuItem[];
    }

    /**
     * Meganav menu entry
     */
    export interface IMeganavItem extends IMenuFields, NICmsContentComponent.IData {
        type: MeganavItemType;
        /**
         * Icon
         */
        meganavItemIcon?: string;
        /**
         * Background color
         */
        meganavItemBackground?: string;
        /**
         * Text color
         */
        meganavItemColor?: string;
        /**
         * Submenu type
         */
        submenuType: MeganavSubmenuType;
        /**
         * Columns type
         */
        columns?: IMeganavItemSubmenuColumn[];
        /**
         * Brand items title
         */
        brandItemsTitle?: string;
        /**
         * Brand items type
         */
        brandItems?: IMeganavItemSubmenuBrandItem[];
        /**
         * Brand items view all link
         */
        brandItemsViewAllLink?: string;
        /**
         * Brand items view all text
         */
        brandItemsViewAllText?: string;
    }

    export type MeganavItemType = 'link' | 'divider';

    export type MeganavItemDividerType = 'line' | 'stretch';

    /**
     * Submenu column type
     */
    export type MeganavSubmenuType = 'columns' | 'brandItems';

    /**
     * Submenu column type
     */
    export type MeganavSubmenuColumnsType = 'image' | 'text';

    /**
     * Submenu column item type
     */
    export type MeganavSubmenuColumnsItemType = 'image' | 'text';

    /**
     * Submenu column item entry
     */
    export interface IMeganavItemSubmenuColumnItem {
        type: MeganavSubmenuColumnsItemType;
        title?: string;
        links?: IMeganavItemSubmenuLink[];
        viewAllText?: string;
        viewAllLink?: string;
        image?: string;
        ratio?: string;
    }

    /**
     * Text submenu link entry
     */
    export interface IMeganavItemSubmenuLink {
        text: string;
        link?: string;
    }

    /**
     * Submenu column entry
     */
    export interface IMeganavItemSubmenuColumn {
        width?: number;
        items?: IMeganavItemSubmenuColumnItem[];
    }

    /**
     * Submenu brand item entry
     */
    export interface IMeganavItemSubmenuBrandItem {
        image?: string;
        title?: string;
        link?: string;
    }
    /**
     * Submenu column
     */
    export interface IMeganavItemSubmenu {
        /**
         * Column type — defaults to 'image' for backward compatibility
         */
        type?: MeganavSubmenuType;
    }

    interface IMenuFields {
        /**
         * Meganav item text
         */
        text: string;
        /**
         * Meganav item link
         */
        link?: string;
        bold?: boolean;
        divider?: MeganavItemDividerType;
    }

    export const STD_MENU_FIELDS = [
        <NICmsContentField.IChoiceField>{
            key: 'type',
            type: NICmsContentField.EFieldType.choice,
            title: 'Type',
            options: [
                { key: 'link', title: 'Link' },
                { key: 'divider', title: 'Divider' }
            ],
            default: 'link'
        },
        {
            key: 'text',
            type: NICmsContentField.EFieldType.text,
            title: 'Item text',
            default: 'Some link',
            required: true
        },
        {
            key: 'link',
            type: NICmsContentField.EFieldType.text,
            title: 'Link',
            canShow: (d: NPageHeader.IMeganavItem) => d?.type !== 'divider'
        },
        {
            key: 'bold',
            type: NICmsContentField.EFieldType.toggle,
            title: 'Bold',
            default: false,
            canShow: (d: NPageHeader.IMeganavItem) => d?.type !== 'divider'
        },
        {
            key: 'divider',
            type: NICmsContentField.EFieldType.choice,
            title: 'Divider type',
            options: [
                { key: 'line', title: 'Line' },
                { key: 'stretch', title: 'Stretch' }
            ],
            default: 'line',
            canShow: (d: NPageHeader.IMeganavItem) => d?.type === 'divider'
        }
    ];

    export const shopSubmenuItemCommonFields = [
        {
            key: 'title',
            type: NICmsContentField.EFieldType.text,
            title: 'Title',
            required: true
        },
        {
            key: 'link',
            type: NICmsContentField.EFieldType.text,
            title: 'Link'
        },
        {
            key: 'submenuLinkCopy',
            type: NICmsContentField.EFieldType.text,
            title: 'Submenu link copy',
            required: true
        },
        {
            key: 'submenuLink',
            type: NICmsContentField.EFieldType.text,
            title: 'Submenu link'
        }
    ];

    export const shopSubmenuItemFields = [
        ...NPageHeader.shopSubmenuItemCommonFields,
        <NICmsContentField.IRepeaterField>{
            type: NICmsContentField.EFieldType.repeater,
            key: 'submenus',
            title: 'Submenus',
            itemName: 'Item',
            itemNameField: 'title',
            complex: true,
            fields: [
                ...NPageHeader.shopSubmenuItemCommonFields,
                <NICmsContentField.IRepeaterField>{
                    key: 'submenus',
                    type: NICmsContentField.EFieldType.repeater,
                    title: 'Submenus',
                    itemName: 'Item',
                    itemNameField: 'title',
                    complex: true,
                    fields: [
                        {
                            key: 'title',
                            type: NICmsContentField.EFieldType.text,
                            title: 'Title',
                            required: true
                        },
                        {
                            key: 'link',
                            type: NICmsContentField.EFieldType.text,
                            title: 'Link'
                        }
                    ]
                }
            ]
        }
    ];

    export const meganavItemFields = [
        ...NPageHeader.STD_MENU_FIELDS,
        <NICmsContentField.IChoiceField>{
            type: NICmsContentField.EFieldType.choice,
            key: 'meganavItemIcon',
            title: 'Meganav Item Icon',
            options: iconsOptions,
            previewMode: 'icon',
            canShow: (d: NPageHeader.IMeganavItem) => d?.type !== 'divider'
        },
        <NICmsContentField.IChoiceField>{
            key: 'meganavItemBackground',
            type: NICmsContentField.EFieldType.choice,
            title: 'Meganav Item Background',
            required: false,
            options: BgColorOptions,
            previewMode: 'color',
            default: 'bg-col-1',
            description: 'Background color for the meganav item when there are no submenus',
            canShow: (d: NPageHeader.IMeganavItem) => d?.type !== 'divider'
        },
        <NICmsContentField.IChoiceField>{
            key: 'meganavItemColor',
            type: NICmsContentField.EFieldType.choice,
            title: 'Meganav Item Color',
            required: false,
            options: ColorOptions,
            previewMode: 'color',
            default: 'col-1',
            description: 'Text color for the meganav item when there are no submenus',
            canShow: (d: NPageHeader.IMeganavItem) => d?.type !== 'divider'
        },
        <NICmsContentField.IChoiceField>{
            key: 'submenuType',
            type: NICmsContentField.EFieldType.choice,
            title: 'Submenu type',
            options: [
                { key: 'columns', title: 'Columns' },
                { key: 'brandItems', title: 'Brand items' }
            ],
            default: 'columns',
            // Not required: existing CMS submenus predate this field; absent type falls back to the
            // 'columns' @default render branch, so legacy content stays valid without a re-save.
            required: false,
            canShow: (d: NPageHeader.IMeganavItem) => d?.type !== 'divider'
        },
        // columns type
        <NICmsContentField.IRepeaterField>{
            key: 'columns',
            type: NICmsContentField.EFieldType.repeater,
            title: 'Columns',
            itemName: 'Column',
            itemNameField: 'title',
            minSize: 0,
            maxSize: 6,
            default: [],
            complex: true,
            fields: [
                {
                    key: 'width',
                    type: NICmsContentField.EFieldType.choice,
                    title: 'Column width',
                    options: [
                        { key: 2, title: '2 columns' },
                        { key: 3, title: '3 columns' },
                        { key: 4, title: '4 columns' },
                        { key: 6, title: '6 columns' }
                    ],
                    default: 2
                },
                <NICmsContentField.IRepeaterField>{
                    key: 'items',
                    type: NICmsContentField.EFieldType.repeater,
                    title: 'Items',
                    itemName: 'Item',
                    itemNameField: 'title',
                    maxSize: 3,
                    minSize: 0,
                    default: [],
                    complex: true,
                    fields: [
                        {
                            key: 'type',
                            type: NICmsContentField.EFieldType.choice,
                            title: 'Item type',
                            options: [
                                { key: 'text', title: 'Text' },
                                { key: 'image', title: 'Image' }
                            ],
                            default: 'text'
                        },
                        {
                            key: 'image',
                            type: NICmsContentField.EFieldType.image,
                            title: 'Image',
                            canShow: (d: NPageHeader.IMeganavItemSubmenuColumnItem) => d?.type === 'image'
                        },
                        {
                            key: 'ratio',
                            type: NICmsContentField.EFieldType.choice,
                            title: 'Image ratio',
                            options: [
                                { key: '2/3', title: '2/3' },
                                { key: '3/4', title: '3/4' },
                                { key: '4/3', title: '4/3' },
                                { key: '16/9', title: '16/9' }
                            ],
                            default: '4/3',
                            canShow: (d: NPageHeader.IMeganavItemSubmenuColumnItem) => d?.type === 'image'
                        },
                        {
                            key: 'title',
                            type: NICmsContentField.EFieldType.textarea,
                            title: 'Title'
                        },
                        <NICmsContentField.IRepeaterField>{
                            key: 'links',
                            type: NICmsContentField.EFieldType.repeater,
                            title: 'Links',
                            itemName: 'Link',
                            itemNameField: 'text',
                            maxSize: 3,
                            minSize: 0,
                            complex: false,
                            canShow: (d: NPageHeader.IMeganavItemSubmenuColumnItem) => d?.type === 'text',
                            fields: [
                                {
                                    key: 'text',
                                    type: NICmsContentField.EFieldType.text,
                                    title: 'Link text',
                                    default: 'Lorem Ipsum',
                                    required: true
                                },
                                {
                                    key: 'link',
                                    type: NICmsContentField.EFieldType.text,
                                    title: 'Link URL'
                                }
                            ]
                        },
                        {
                            key: 'viewAllText',
                            type: NICmsContentField.EFieldType.text,
                            title: 'View all text'
                        },
                        {
                            key: 'viewAllLink',
                            type: NICmsContentField.EFieldType.text,
                            title: 'View all link'
                        }
                    ]
                }
            ],
            canShow: (d: NPageHeader.IMeganavItem) => d?.submenuType === 'columns' && d?.type !== 'divider'
        },
        // brand items type
        {
            key: 'brandItemsTitle',
            type: NICmsContentField.EFieldType.text,
            title: 'Title',
            canShow: (d: NPageHeader.IMeganavItem) => d?.submenuType === 'brandItems'
        },
        <NICmsContentField.IRepeaterField>{
            key: 'brandItems',
            type: NICmsContentField.EFieldType.repeater,
            title: 'Brand items',
            itemName: 'Brand item',
            itemNameField: 'title',
            complex: true,
            minSize: 0,
            default: [],
            fields: [
                {
                    key: 'image',
                    type: NICmsContentField.EFieldType.image,
                    title: 'Image',
                    required: true
                },
                {
                    key: 'title',
                    type: NICmsContentField.EFieldType.text,
                    title: 'Title'
                },
                {
                    key: 'link',
                    type: NICmsContentField.EFieldType.text,
                    title: 'Link',
                    description: 'Link on the entire item including image and copies'
                }
            ],
            canShow: (d: NPageHeader.IMeganavItem) => d?.submenuType === 'brandItems'
        },
        {
            key: 'brandItemsViewAllText',
            type: NICmsContentField.EFieldType.text,
            title: 'Brand items view all text',
            canShow: (d: NPageHeader.IMeganavItem) => d?.submenuType === 'brandItems'
        },
        {
            key: 'brandItemsViewAllLink',
            type: NICmsContentField.EFieldType.text,
            title: 'Brand items view all link',
            canShow: (d: NPageHeader.IMeganavItem) => d?.submenuType === 'brandItems'
        }
    ];

    export interface IPromobarContentBlock {
        copy: string;
        link: string;
        icon?: string;
    }

    export const MEGANAV_GROUP_ID = 'meganav';

    export const SHOP_SUBMENU_GROUP_ID = 'shop-submenu';

    export const MAGANV_CONTENT_ITEM_COMPONENT_ID = 'page-header-meganav-content-item';
}

@Component({
    selector: 'page-header',
    templateUrl: 'page-header.html',
    styleUrls: ['page-header.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
@ICmsComponent<PageHeaderComponent>({
    id: NPageHeader.BLOCK_ID,
    name: 'Page Header',
    description: 'Page header block',
    onDemandOnly: true,
    block: {
        url: NPageHeader.BLOCK_ID,
        scope: 'website',
        autoload: 'bootstrap'
    },
    fields: [
        // general
        {
            type: NICmsContentField.EFieldType.label,
            key: 'sectionGeneral',
            title: 'General'
        },
        {
            key: 'logo',
            type: NICmsContentField.EFieldType.image,
            title: 'Logo Image',
            required: true
        },
        <NICmsContentField.IRepeaterField>{
            type: NICmsContentField.EFieldType.repeater,
            key: 'meganav',
            title: 'Menu Items',
            itemName: 'Item',
            itemNameField: 'text',
            complex: true,
            fields: NPageHeader.meganavItemFields
        },
        {
            type: NICmsContentField.EFieldType.label,
            key: 'shopSubmenu',
            title: 'Shop submenu'
        },
        <NICmsContentField.IChoiceField>{
            type: NICmsContentField.EFieldType.choice,
            key: 'shopSubmenuIcon',
            title: 'Shop submenu icon',
            options: iconsOptions,
            previewMode: 'icon'
        },
        <NICmsContentField.IRepeaterField>{
            type: NICmsContentField.EFieldType.repeater,
            key: 'shopSubmenu',
            title: 'Shop submenu',
            itemName: 'Item',
            itemNameField: 'title',
            complex: true,
            fields: NPageHeader.shopSubmenuItemFields
        },
        {
            type: NICmsContentField.EFieldType.label,
            key: 'promotionalMessage',
            title: 'Promotional Message'
        },
        {
            key: 'promoImage',
            type: NICmsContentField.EFieldType.image,
            title: 'Promotional Image',
            required: true
        },
        {
            key: 'promoText',
            type: NICmsContentField.EFieldType.text,
            title: 'Promotional Text',
            required: true
        },
        {
            key: 'promoButtonCopy',
            type: NICmsContentField.EFieldType.text,
            title: 'Promotional Button Copy',
            required: true
        },
        {
            key: 'promoButtonLink',
            type: NICmsContentField.EFieldType.text,
            title: 'Promotional Button Link',
            required: true
        },
        {
            type: NICmsContentField.EFieldType.label,
            key: 'sectionPromobanner',
            title: 'Promo banner'
        },
        {
            key: 'promobannerEnabled',
            type: NICmsContentField.EFieldType.toggle,
            title: 'Enabled',
            required: false
        },
        <NICmsContentField.IChoiceField>{
            key: 'promobannerBackround',
            type: NICmsContentField.EFieldType.choice,
            title: 'Background colour',
            required: false,
            options: BgColorOptions,
            previewMode: 'color',
            default: 'bg-col-6',
            canShow: (data: NPageHeader.IBlockDataContent) => data?.promobannerEnabled
        },
        <NICmsContentField.IChoiceField>{
            key: 'promobannerColor',
            type: NICmsContentField.EFieldType.choice,
            title: 'Text colour',
            required: false,
            options: () => app.injector.get<NICmsClientConfig.IConfig>(ICMS_CLIENT_CONFIG).colors,
            previewMode: 'color',
            default: 'col-w',
            canShow: (data: NPageHeader.IBlockDataContent) => data?.promobannerEnabled
        },
        <NICmsContentField.IRepeaterField<NPageHeader.IPromobarContentBlock>>{
            key: 'promobannerBlocks',
            type: NICmsContentField.EFieldType.repeater,
            title: 'Slide content',
            required: true,
            canShow: (data: NPageHeader.IBlockDataContent) => data?.promobannerEnabled,
            itemName: 'element',
            itemNameField: 'copy',
            fields: [
                {
                    key: 'copy',
                    type: NICmsContentField.EFieldType.text,
                    title: 'Copy',
                    required: true
                },
                {
                    key: 'link',
                    type: NICmsContentField.EFieldType.text,
                    title: 'Link',
                    required: true
                },
                {
                    type: NICmsContentField.EFieldType.choice,
                    title: 'Icon',
                    key: 'icon',
                    options: iconsOptions,
                    previewMode: 'icon'
                }
            ]
        },
        { key: 'tabslabel', type: NICmsContentField.EFieldType.label, title: 'Brand tabs' },
        {
            key: 'hideTab',
            type: NICmsContentField.EFieldType.toggle,
            title: 'Hide tab'
        },
        <NICmsContentField.IToggleField>{
            key: 'firstActivated',
            type: NICmsContentField.EFieldType.toggle,
            title: 'Activated tab',
            labels: { true: 'First tab activated', false: 'Second tab activated' },
            default: true,
            canShow: (data: NPageHeader.IBlockDataContent) => !data?.hideTab
        },
        {
            key: 'firstTabImage',
            type: NICmsContentField.EFieldType.image,
            title: 'First tab image',
            canShow: (data: NPageHeader.IBlockDataContent) => !data?.hideTab
        },
        {
            key: 'firstTabLink',
            type: NICmsContentField.EFieldType.text,
            title: 'First tab link',
            canShow: (data: NPageHeader.IBlockDataContent) => !data?.hideTab
        },
        {
            key: 'secondTabImage',
            type: NICmsContentField.EFieldType.image,
            title: 'Second tab image',
            canShow: (data: NPageHeader.IBlockDataContent) => !data?.hideTab
        },
        {
            key: 'secondTabLink',
            type: NICmsContentField.EFieldType.text,
            title: 'Second tab link',
            canShow: (data: NPageHeader.IBlockDataContent) => !data?.hideTab
        },
        {
            key: 'thirdTabImage',
            type: NICmsContentField.EFieldType.image,
            title: 'Third tab image',
            canShow: (data: NPageHeader.IBlockDataContent) => !data?.hideTab
        },
        {
            key: 'thirdTabLink',
            type: NICmsContentField.EFieldType.text,
            title: 'Third tab link',
            canShow: (data: NPageHeader.IBlockDataContent) => !data?.hideTab
        },
        <NICmsContentField.IRepeaterField>{
            type: NICmsContentField.EFieldType.repeater,
            key: 'topLinks',
            title: 'Top links',
            itemName: 'Link',
            itemNameField: 'text',
            complex: false,
            fields: [
                {
                    key: 'text',
                    type: NICmsContentField.EFieldType.text,
                    title: 'Text',
                    required: true
                },
                {
                    key: 'link',
                    type: NICmsContentField.EFieldType.text,
                    title: 'Link',
                    required: true
                }
            ]
        }
    ]
})
// extends AbstractCmsBlock<NPageHeader.IBlockData, NPageHeader.IBlockDataContent>
export class PageHeaderComponent extends UiComponent implements NICmsContentComponent.IComponent, OnInit, OnDestroy {
    @Input()
    offCanvasState!: BehaviorSubject<NPageOffCanvas.EOpenState>;

    /**
     * Minibasket modal id
     */
    readonly minibasketModalId = NCmsMinibasket.BLOCK_ID;

    /**
     * If content is set
     */
    hasContent$: BehaviorSubject<boolean> = new BehaviorSubject(false);

    /**
     * If bottom shadow class is applied
     */
    private hasBottomShadow = false;

    protected showTopBanner = true;
    protected isSearchVisible = false;
    protected vatSelected = false;

    readonly headerContentId = PAGE_HEADER_CONTENT_ELEMENT;
    readonly toggleGroupId = NPageHeader.MEGANAV_GROUP_ID;
    protected BRANCH_MODAL_ID = NCmsBranchCollection.MODAL_ID;
    readonly DELIVERY_MODAL_ID = NCmsDelivery.MODAL_ID;

    protected isCustomerLoaded = false;
    protected deliveryPostcode = inject(DeliveryPostcodeService);
    private branchService = inject(BranchService);
    private branchNumber: number | undefined;
    private postcodeEffect = effect(() => {
        const postcode = this.deliveryPostcode.postcode();
        this.detectChanges();
    });

    constructor(
        private renderer2: Renderer2,
        private elementRef: ElementRef,
        private toggleService: ToggleService,
        private scrollService: ScrollService,
        private priceService: PriceService,
        private meta: Meta,
        private afterContentInitDirectiveService: AfterContentInitDirectiveService,
        private customerModel: CustomerModel
    ) {
        super();
        effect(() => {
            if (this.branchNumber !== this.branchService.branchNumber()) {
                this.branchNumber = this.branchService.branchNumber();
                this.detectChanges();
            }
        });
    }

    @ICmsContent({
        type: 'blockContent'
    })
    content?: NPageHeader.IBlockDataContent;

    /**
     * Hook to override when sticky should be off
     */
    get isStickyOff() {
        const currentModel = this.meta.model;
        return (
            currentModel?.type === 'page' &&
            this.client.sizeId < 2 &&
            (currentModel as ICmsPage).data?.pageType === NPageStoreFinderPageType.typeId
        );
    }

    /**
     * Init override to set up subscriptions
     */
    override ngOnInit() {
        this.loadCustomerData();
        super.ngOnInit();
        const catalogPricesInclTax = !!this.priceService.showCatalogPricesInclTax;
        if (!!catalogPricesInclTax) {
            this.vatSelected = catalogPricesInclTax;
        }

        // trigger change detection after content is initiated
        this.subscriptions.add(
            this.afterContentInitDirectiveService.loaded.subscribe(value => {
                if (value) {
                    this.detectChanges();
                }
            })
        );

        // apply bottom shadow when header is sticky
        this.subscriptions.add(this.scrollService.scroll$.subscribe((event: ScrollEvent) => this.updateBottomShadow(event.scrollTop > 0)));
    }

    private async loadCustomerData() {
        await this.customerModel.load();
        await this.branchService.load();
        this.isCustomerLoaded = true;
        this.detectChanges();
    }

    protected onSearchFormVisibilityChange(isSearchVisible: boolean) {
        isSearchVisible
            ? this.renderer2.addClass(this.elementRef.nativeElement, '_searchVisible')
            : this.renderer2.removeClass(this.elementRef.nativeElement, '_searchVisible');
    }

    openOffCanvas() {
        this.offCanvasState.next(NPageOffCanvas.EOpenState.openMenu);
    }

    openOffCanvasSearch() {
        this.offCanvasState.next(NPageOffCanvas.EOpenState.openSearch);
        this.toggleService.open(PageOffCanvasSearchFormComponent.toggleId);
    }

    override detectChanges() {
        if (this.initiated) {
            const hasContent = !!this.content;
            if (this.hasContent$.value !== hasContent) {
                this.changeDetectorRef.detectChanges();
                this.hasContent$.next(!!hasContent);
                return;
            }
        }
        super.detectChanges();
    }

    /**
     * Updates bottom shadow class
     */
    private updateBottomShadow(hasBottomShadow = this.hasBottomShadow) {
        if (this.isStickyOff) {
            hasBottomShadow = false;
        }
        if (this.hasBottomShadow !== hasBottomShadow) {
            hasBottomShadow
                ? this.renderer2.addClass(this.elementRef.nativeElement, '_sticky')
                : this.renderer2.removeClass(this.elementRef.nativeElement, '_sticky');
            this.hasBottomShadow = hasBottomShadow;
        }
    }

    protected toggleVat() {
        this.priceService.showCatalogPricesInclTax = this.vatSelected;
        // this.detectChanges();
    }

    protected get branchName(): string {
        return this.branchService.branchNumber() ? this.branchService.toName(this.branchService.branchNumber() || 0) : '';
    }
}
